/*
  AB#21901 — did it work? The write side of ELSA's outcome records.

  Two fire-and-forget calls: what a person did with a suggestion (actioned on
  the queue or the order page; the backend records snooze/dismiss itself), and
  the optional one-tap rating afterwards. Neither may ever slow down or fail
  the thing the person was doing, so both swallow errors. The backend keeps
  one row per person × order item × suggestion × reading, so calling twice is
  harmless.
*/
import { http } from '@/interceptors/axiosRequestInterceptor';

/** 'finance_queue' (AB#21998): a finance card, recorded under its kind of issue. */
export type OutcomeSource = 'needs_attention' | 'order_page' | 'finance_queue';
export type OutcomeFeedback = 'yes' | 'no' | 'wrong_state' | 'already_done';

export interface SuggestionRef {
  orderItemId: number;
  code: string;
  label?: string | null;
}

/** Codes that are a real suggestion; navigation alone is not one. */
// open_invoices (AB#21998) opens a finance card's order; the card itself is what is tracked.
const NOT_A_SUGGESTION = new Set(['view_order', 'open_order', 'open_elsa', 'open_invoices']);

export const isRecordableSuggestion = (code: string | null | undefined): code is string =>
  typeof code === 'string' && /^[a-z_]+$/.test(code) && !NOT_A_SUGGESTION.has(code);

export const recordSuggestionAction = (ref: SuggestionRef, source: OutcomeSource): void => {
  if (!isRecordableSuggestion(ref.code) || !ref.orderItemId) return;
  void http
    .post('elsa/suggestions/event', {
      order_item_id: ref.orderItemId,
      suggestion_code: ref.code,
      action: 'actioned',
      source,
      label: ref.label ?? null,
    })
    .catch(() => undefined);
};

export const recordSuggestionFeedback = (ref: SuggestionRef, feedback: OutcomeFeedback): void => {
  if (!isRecordableSuggestion(ref.code) || !ref.orderItemId) return;
  void http
    .post('elsa/suggestions/feedback', {
      order_item_id: ref.orderItemId,
      suggestion_code: ref.code,
      feedback,
    })
    .catch(() => undefined);
};

/** The four answers the prompt offers, in order. */
export const FEEDBACK_CHOICES: { value: OutcomeFeedback; label: string }[] = [
  { value: 'yes', label: '👍 Yes' },
  { value: 'no', label: '👎 No' },
  { value: 'wrong_state', label: 'Wrong order state' },
  { value: 'already_done', label: 'Already done' },
];
